import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { getProductImageUrl, useProductImageFallback } from '../../utils/productImages';
import './OrderDetails.css';

const OrderDetails = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    order_state: '',
    PO_Number: '',
    More_Details: '',
    billing_address: '',
    shipping_address: '',
    shipping_cost: '',
    taxes: '',
    delivery_time: '',
    managed_by: '',
    items: [],
  });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        const token = localStorage.getItem('admin_token');
        if (!token) {
          setError('Please log in to view order details');
          navigate('/admin-login');
          return;
        }

        const response = await axios.get(`https://valleyapi-dr9y.onrender.com/api/cart/admin/orders/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setOrder(response.data);
        setEditForm({
          order_state: response.data.order_state || '',
          PO_Number: response.data.PO_Number || '',
          More_Details: response.data.More_Details || '',
          billing_address: response.data.billing_address || '',
          shipping_address: response.data.shipping_address || '',
          shipping_cost: response.data.shipping_cost || '',
          taxes: response.data.taxes || '',
          delivery_time: response.data.delivery_time || '',
          managed_by: response.data.managed_by || '',
          items: response.data.items.map(item => ({
            idorder_item: item.idorder_item,
            product_price: item.product_price || '',
            More_Details: item.More_Details || '',
          })),
        });
        setError('');
      } catch (err) {
        console.error('Error fetching order details:', err);
        setError(err.response?.data?.message || 'Failed to load order details');
      }
    };

    fetchOrderDetails();
  }, [id, navigate]);

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete order #${id}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        setError('Please log in to delete order');
        navigate('/admin-login');
        return;
      }

      await axios.delete(`https://valleyapi-dr9y.onrender.com/api/cart/admin/orders/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setError('');
      navigate('/admin/orders', { replace: true });
    } catch (err) {
      console.error('Error deleting order:', err);
      setError(err.response?.data?.message || 'Failed to delete order');
    }
  };

  const handleDownload = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        setError('Please log in to download order document');
        navigate('/admin-login');
        return;
      }

      const response = await axios.get(`https://valleyapi-dr9y.onrender.com/api/cart/admin/orders/${id}/download`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob',
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      saveAs(blob, `Order_${id}.docx`);
      setError('');
    } catch (err) {
      console.error('Error downloading document:', err);
      setError(err.response?.data?.message || 'Failed to download document');
    }
  };

  const handleSendNotification = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        setError('Please log in to send notification');
        navigate('/admin-login');
        return;
      }

      const response = await axios.post(
        `https://valleyapi-dr9y.onrender.com/api/cart/admin/orders/${id}/notify`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      setError('');
      alert('Notification sent to buyer successfully');
    } catch (err) {
      console.error('Error sending notification:', err);
      setError(err.response?.data?.message || 'Failed to send notification');
    }
  };

  const handleEditChange = (e, itemId = null) => {
    const { name, value } = e.target;
    if (itemId) {
      setEditForm((prev) => ({
        ...prev,
        items: prev.items.map(item =>
          item.idorder_item === itemId ? { ...item, [name]: value } : item
        ),
      }));
    } else {
      setEditForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        setError('Please log in to edit order');
        navigate('/admin-login');
        return;
      }

      const response = await axios.put(
        `https://valleyapi-dr9y.onrender.com/api/cart/admin/orders/${id}`,
        {
          order_state: editForm.order_state,
          PO_Number: editForm.PO_Number || null,
          More_Details: editForm.More_Details || null,
          billing_address: editForm.billing_address,
          shipping_address: editForm.shipping_address,
          shipping_cost: parseFloat(editForm.shipping_cost),
          taxes: parseFloat(editForm.taxes),
          delivery_time: editForm.delivery_time || null,
          managed_by: editForm.managed_by || null,
          items: editForm.items.map(item => ({
            idorder_item: item.idorder_item,
            product_price: parseFloat(item.product_price),
            More_Details: item.More_Details || null,
          })),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      setOrder({
        ...order,
        order_state: editForm.order_state,
        PO_Number: editForm.PO_Number,
        More_Details: editForm.More_Details,
        billing_address: editForm.billing_address,
        shipping_address: editForm.shipping_address,
        shipping_cost: parseFloat(editForm.shipping_cost),
        taxes: parseFloat(editForm.taxes),
        subtotal: response.data.newSubtotal,
        total: response.data.newTotal,
        delivery_time: editForm.delivery_time,
        managed_by: editForm.managed_by,
        items: order.items.map(item => ({
          ...item,
          product_price: editForm.items.find(i => i.idorder_item === item.idorder_item).product_price,
          More_Details: editForm.items.find(i => i.idorder_item === item.idorder_item).More_Details,
        })),
      });
      setShowEditModal(false);
      setError('');
    } catch (err) {
      console.error('Error updating order:', err);
      setError(err.response?.data?.message || 'Failed to update order');
    }
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditForm({
      order_state: order?.order_state || '',
      PO_Number: order?.PO_Number || '',
      More_Details: order?.More_Details || '',
      billing_address: order?.billing_address || '',
      shipping_address: order?.shipping_address || '',
      shipping_cost: order?.shipping_cost || '',
      taxes: order?.taxes || '',
      delivery_time: order?.delivery_time || '',
      managed_by: order?.managed_by || '',
      items: order?.items.map(item => ({
        idorder_item: item.idorder_item,
        product_price: item.product_price || '',
        More_Details: item.More_Details || '',
      })) || [],
    });
  };

  if (!order && !error) {
    return <div className="order-details-container">Loading...</div>;
  }

  return (
    <div className="order-details-container">
      <h2 className="order-details-title">Order Details - Order #{id}</h2>
      {error && <p className="order-details-error">{error}</p>}
      {order ? (
        <div className="order-details-info">
          <p><strong>Order Nro:</strong> {order.idorders}</p>
          <p><strong>Company Name:</strong> {order.companyName}</p>
          <p><strong>Order State:</strong> {order.order_state}</p>
          <p><strong>Created On:</strong> {new Date(order.date).toLocaleString()}</p>
          <p><strong>Billing Address:</strong> {order.billing_address}</p>
          <p><strong>Shipping Address:</strong> {order.shipping_address}</p>
          {order.PO_Number && <p><strong>PO Number:</strong> {order.PO_Number}</p>}
          {order.More_Details && <p><strong>More Details:</strong> {order.More_Details}</p>}
          {order.delivery_time && <p><strong>Delivery Date:</strong> {new Date(order.delivery_time).toLocaleDateString()}</p>}
          <p><strong>Managed By:</strong> {order.managed_by || '-'}</p>
          <p><strong>Subtotal:</strong> ${parseFloat(order.subtotal).toFixed(2)}</p>
          <p><strong>Shipping Cost:</strong> ${parseFloat(order.shipping_cost).toFixed(2)}</p>
          <p><strong>Taxes:</strong> ${parseFloat(order.taxes).toFixed(2)}</p>
          <p><strong>Total:</strong> ${parseFloat(order.total).toFixed(2)}</p>
          <h3 className="order-details-subtitle">Order Items</h3>
          {order.items && order.items.length > 0 ? (
            <div className="order-details-table-wrapper">
              <table className="order-details-table">
                <thead className="order-details-table-header">
                  <tr>
                    <th className="order-details-table-header-cell">Image</th>
                    <th className="order-details-table-header-cell">Product Name</th>
                    <th className="order-details-table-header-cell">Description</th>
                    <th className="order-details-table-header-cell">Brand</th>
                    <th className="order-details-table-header-cell">Price</th>
                    <th className="order-details-table-header-cell">Quantity</th>
                    <th className="order-details-table-header-cell">Total</th>
                    <th className="order-details-table-header-cell">More Details</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.map((item) => (
                    <tr key={item.idorder_item} className="order-details-table-row">
                      <td className="order-details-table-cell">
                        <img
                          src={getProductImageUrl(item.product_image)}
                          onError={useProductImageFallback}
                          alt={item.product_name}
                          className="order-details-table-image"
                        />
                      </td>
                      <td className="order-details-table-cell">{item.product_name}</td>
                      <td className="order-details-table-cell">{item.product_description}</td>
                      <td className="order-details-table-cell">{item.product_brand}</td>
                      <td className="order-details-table-cell">${parseFloat(item.product_price).toFixed(2)}</td>
                      <td className="order-details-table-cell">{item.quantity}</td>
                      <td className="order-details-table-cell">
                        ${(parseFloat(item.product_price) * item.quantity).toFixed(2)}
                      </td>
                      <td className="order-details-table-cell">{item.More_Details || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>No items found for this order.</p>
          )}
          <div className="order-details-actions">
            <button
              onClick={() => setShowEditModal(true)}
              className="order-details-button"
            >
              <i className="fas fa-edit"></i> Edit Order
            </button>
            <button
              onClick={handleDelete}
              className="order-details-button delete"
            >
              Delete Order
            </button>
            <button
              onClick={handleDownload}
              className="order-details-button"
            >
              <i className="fas fa-download"></i> Download Order
            </button>
            <button
              onClick={handleSendNotification}
              className="order-details-button"
            >
              <i className="fas fa-envelope"></i> Send Notification to Buyer
            </button>
          </div>
        </div>
      ) : (
        <p>Order not found.</p>
      )}

      {showEditModal && (
        <div className="order-details-modal">
          <div className="order-details-modal-content">
            <h3 className="order-details-modal-title">Edit Order #{id}</h3>
            <form onSubmit={handleEditSubmit} className="order-details-form">
              <div className="order-details-form-group">
                <label htmlFor="order_state" className="order-details-form-label">
                  Order State
                </label>
                <select
                  id="order_state"
                  name="order_state"
                  value={editForm.order_state}
                  onChange={handleEditChange}
                  className="order-details-form-input"
                  required
                >
                  <option value="All">All States</option>
                  <option value="Quote Requested">Quote Requested</option>
                  <option value="Quote Submitted">Quote Submitted</option>
                  <option value="Waiting for Payment">Waiting for Payment</option>
                  <option value="Processing">Processing</option>
                  <option value="Shipped">Shipped</option>
                  <option value="Delivered">Delivered</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
              <div className="order-details-form-group">
                <label htmlFor="PO_Number" className="order-details-form-label">
                  PO Number
                </label>
                <input
                  id="PO_Number"
                  name="PO_Number"
                  type="text"
                  value={editForm.PO_Number}
                  onChange={handleEditChange}
                  maxLength={30}
                  className="order-details-form-input"
                  placeholder="Enter PO Number"
                />
              </div>
              <div className="order-details-form-group">
                <label htmlFor="More_Details" className="order-details-form-label">
                  More Details
                </label>
                <textarea
                  id="More_Details"
                  name="More_Details"
                  value={editForm.More_Details}
                  onChange={handleEditChange}
                  maxLength={255}
                  className="order-details-form-input"
                  placeholder="Enter additional details"
                />
              </div>
              <div className="order-details-form-group">
                <label htmlFor="billing_address" className="order-details-form-label">
                  Billing Address
                </label>
                <input
                  id="billing_address"
                  name="billing_address"
                  type="text"
                  value={editForm.billing_address}
                  onChange={handleEditChange}
                  maxLength={255}
                  className="order-details-form-input"
                  placeholder="Enter billing address"
                  required
                />
              </div>
              <div className="order-details-form-group">
                <label htmlFor="shipping_address" className="order-details-form-label">
                  Shipping Address
                </label>
                <input
                  id="shipping_address"
                  name="shipping_address"
                  type="text"
                  value={editForm.shipping_address}
                  onChange={handleEditChange}
                  maxLength={255}
                  className="order-details-form-input"
                  placeholder="Enter shipping address"
                  required
                />
              </div>
              <div className="order-details-form-group">
                <label htmlFor="shipping_cost" className="order-details-form-label">
                  Shipping Cost
                </label>
                <input
                  id="shipping_cost"
                  name="shipping_cost"
                  type="number"
                  step="0.01"
                  min="0"
                  value={editForm.shipping_cost}
                  onChange={handleEditChange}
                  className="order-details-form-input"
                  placeholder="Enter shipping cost"
                  required
                />
              </div>
              <div className="order-details-form-group">
                <label htmlFor="taxes" className="order-details-form-label">
                  Taxes
                </label>
                <input
                  id="taxes"
                  name="taxes"
                  type="number"
                  step="0.01"
                  min="0"
                  value={editForm.taxes}
                  onChange={handleEditChange}
                  className="order-details-form-input"
                  placeholder="Enter taxes"
                  required
                />
              </div>
              <div className="order-details-form-group">
                <label htmlFor="delivery_time" className="order-details-form-label">
                  Delivery Date
                </label>
                <input
                  id="delivery_time"
                  name="delivery_time"
                  type="date"
                  value={editForm.delivery_time}
                  onChange={handleEditChange}
                  className="order-details-form-input"
                  placeholder="Select delivery date"
                />
              </div>
              <div className="order-details-form-group">
                <label htmlFor="managed_by" className="order-details-form-label">
                  Managed By
                </label>
                <input
                  id="managed_by"
                  name="managed_by"
                  type="text"
                  value={editForm.managed_by}
                  onChange={handleEditChange}
                  maxLength={255}
                  className="order-details-form-input"
                  placeholder="Enter manager name"
                />
              </div>
              <h4 className="order-details-form-subtitle">Order Items</h4>
              <div className="order-details-modal-table-wrapper">
                <table className="order-details-modal-table">
                  <thead className="order-details-modal-table-header">
                    <tr>
                      <th className="order-details-modal-table-header-cell">Product Name</th>
                      <th className="order-details-modal-table-header-cell">Price</th>
                      <th className="order-details-modal-table-header-cell">More Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {editForm.items.map((item) => (
                      <tr key={item.idorder_item} className="order-details-modal-table-row">
                        <td className="order-details-modal-table-cell">
                          {order.items.find(i => i.idorder_item === item.idorder_item)?.product_name}
                        </td>
                        <td className="order-details-modal-table-cell">
                          <input
                            type="number"
                            name="product_price"
                            step="0.01"
                            min="0"
                            value={item.product_price}
                            onChange={(e) => handleEditChange(e, item.idorder_item)}
                            className="order-details-modal-table-input"
                            required
                          />
                        </td>
                        <td className="order-details-modal-table-cell">
                          <textarea
                            name="More_Details"
                            value={item.More_Details}
                            onChange={(e) => handleEditChange(e, item.idorder_item)}
                            maxLength={255}
                            className="order-details-modal-table-input"
                            placeholder="Enter item details"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="order-details-modal-buttons">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="order-details-modal-cancel"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="order-details-modal-save"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetails;
